import type { Microsoft365AdminDto } from '@dbrain/shared';
import { Alert, Badge, Button, ConfirmDialog, Field, Icons, RadioCard, Skeleton, TextInput } from '@dbrain/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { useMicrosoft365Admin, useMicrosoft365AdminConsent, useMicrosoft365Test, useSaveMicrosoft365Setup } from '../../../api/microsoft365-admin.js';
import { ErrorAlert } from '../../ErrorAlert.js';
import {
  formOf,
  resetsAccounts,
  SETUP_STEPS,
  setupBody,
  setupProblems,
  setupUnchanged,
  stepOf,
  stepOpen,
  SUMMARY_PATH,
  type SetupForm,
  type SetupProblem,
  type SetupStep,
} from './model.js';
import { BuiltinTile, CopyButton, TestOutcome } from './parts.js';

/**
 * Yönetim › Entegrasyonlar › Microsoft 365 › Kur (MsYonetimKurulum): four steps, the current one in the address
 * (?step=, D-100). 1 what to add to the firm's own Entra registration (with a folding "Adım adım": we host no
 * pictures, so no "Resimli rehber"), 2 its ids, the write-only client secret, the secret's optional end date, the
 * redirect address from the server and the sign-in method, 3 admin consent at Microsoft, 4 the test in plain words.
 */
const STEP_KEYS: Record<SetupStep, 's1' | 's2' | 's3' | 's4'> = { 1: 's1', 2: 's2', 3: 's3', 4: 's4' };

export function Microsoft365Setup() {
  const admin = useMicrosoft365Admin();
  const [params, setParams] = useSearchParams();
  if (admin.isPending) return <Skeleton block />;
  if (admin.error) return <ErrorAlert error={admin.error} />;
  const dto = admin.data;
  return <Wizard dto={dto} step={stepOf(params.get('step'), dto)} go={(next) => setParams({ step: String(next) })} />;
}

function Wizard({ dto, step, go }: { dto: Microsoft365AdminDto; step: SetupStep; go: (step: SetupStep) => void }) {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <div className="m365a-admin m365a-setup" data-testid="m365-setup" data-step={step}>
      <header className="m365a-setup__head">
        <BuiltinTile />
        <div>
          <h2 className="m365a-setup__title">{t('microsoft365.admin.setup.title')}</h2>
          <p className="m365a-setup__intro">{t('microsoft365.admin.setup.intro')}</p>
        </div>
      </header>
      <Stepper dto={dto} step={step} />
      <section className="admin-card m365a-step" aria-labelledby={titleId} data-testid={`m365-step-${step}`}>
        <h3 id={titleId} className="m365a-step__title">
          {t('microsoft365.admin.setup.stepTitle', { n: step, title: t(`microsoft365.admin.setup.steps.${STEP_KEYS[step]}`) })}
        </h3>
        {step === 1 ? <RegisterStep onNext={() => go(2)} /> : null}
        {step === 2 ? <DetailsStep dto={dto} onBack={() => go(1)} onDone={() => go(3)} /> : null}
        {step === 3 ? <ConsentStep dto={dto} onBack={() => go(2)} onNext={() => go(4)} /> : null}
        {step === 4 ? <TestStep dto={dto} onBack={() => go(3)} onFix={go} /> : null}
      </section>
    </div>
  );
}

/** The four steps in a row: done ones ticked, the current one dark; consent and the test open once saved. */
function Stepper({ dto, step }: { dto: Microsoft365AdminDto; step: SetupStep }) {
  const { t } = useTranslation();
  return (
    <nav className="admin-card m365a-steps" aria-label={t('microsoft365.admin.setup.stepsLabel')}>
      <ol className="m365a-steps__list">
        {SETUP_STEPS.map((n) => {
          const state = n < step ? 'done' : n === step ? 'current' : 'next';
          const content = (
            <>
              <span className="m365a-steps__mark" aria-hidden="true">
                {state === 'done' ? <Icons.check size={14} /> : n}
              </span>
              <span className="m365a-steps__label">{t(`microsoft365.admin.setup.steps.${STEP_KEYS[n]}`)}</span>
              {state === 'done' ? <span className="ui-visually-hidden"> ({t('microsoft365.admin.setup.stepDone')})</span> : null}
            </>
          );
          return (
            <li key={n} className={`m365a-steps__item m365a-steps__item--${state}`} aria-current={state === 'current' ? 'step' : undefined} data-testid={`m365-steps-${n}`}>
              {state !== 'current' && stepOpen(n, dto) ? (
                <Link className="m365a-steps__link" to={{ search: `?step=${n}` }}>
                  {content}
                </Link>
              ) : (
                <span className="m365a-steps__link">{content}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

const REGISTER_ITEMS = [
  { key: 'redirect', icon: Icons.integrations },
  { key: 'permissions', icon: Icons.lock },
  { key: 'consent', icon: Icons.shield },
  { key: 'secret', icon: Icons.key },
] as const;

const GUIDE_STEPS = ['s1', 's2', 's3', 's4', 's5', 's6'] as const;

/** 1 · What the firm adds to its Entra registration (an existing one or a new one). */
function RegisterStep({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation();
  return (
    <>
      <p className="m365a-step__intro">{t('microsoft365.admin.setup.register.intro')}</p>
      <ul className="m365a-todo">
        {REGISTER_ITEMS.map(({ key, icon: Icon }) => (
          <li key={key} className="m365a-todo__item" data-testid={`m365-todo-${key}`}>
            <span className="m365a-todo__icon" aria-hidden="true">
              <Icon size={16} />
            </span>
            <span className="m365a-todo__text">
              <strong>{t(`microsoft365.admin.setup.register.${key}.title`)}</strong>
              <span>{t(`microsoft365.admin.setup.register.${key}.text`)}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="m365a-note">
        <Icons.info size={16} />
        <span>{t('microsoft365.admin.setup.register.noAppPermissions')}</span>
      </p>
      <details className="m365a-guide" data-testid="m365-guide">
        <summary>{t('microsoft365.admin.setup.register.guide')}</summary>
        <ol className="m365a-guide__steps">
          {GUIDE_STEPS.map((key) => (
            <li key={key}>{t(`microsoft365.admin.setup.register.guideSteps.${key}`)}</li>
          ))}
        </ol>
      </details>
      <footer className="m365a-step__actions">
        <Link className="ui-button ui-button--ghost" to="/admin/integrations" data-testid="m365-setup-cancel">
          {t('common.cancel')}
        </Link>
        <Button onClick={onNext} data-testid="m365-step-next">
          {t('microsoft365.admin.setup.next')}
        </Button>
      </footer>
    </>
  );
}

/**
 * 2 · The registration's ids, the client secret (write-only: never filled in, sent only when typed, cleared once
 * saved), its optional end date, the redirect address to add and the sign-in method. Saving another tenant or app
 * asks first: everyone connected to the old one loses their connection.
 */
function DetailsStep({ dto, onBack, onDone }: { dto: Microsoft365AdminDto; onBack: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<SetupForm>(() => formOf(dto));
  const [tried, setTried] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // folded unless the address needs "Kodla" or it is already chosen
  const [methodOpen] = useState(() => !dto.redirectUriAccepted || dto.signInMethod === 'device_code');
  const save = useSaveMicrosoft365Setup(() => setupBody(form));
  const problems = setupProblems(form, dto.hasSecret);
  const problem = (field: SetupProblem) => tried && problems.includes(field);
  const set = (patch: Partial<SetupForm>) => setForm((current) => ({ ...current, ...patch }));
  const run = () =>
    save.mutate(undefined, {
      onSuccess: () => {
        set({ clientSecret: '' });
        setConfirming(false);
        onDone();
      },
    });
  const submit = () => {
    setTried(true);
    if (problems.length > 0) return;
    if (setupUnchanged(dto, form)) return onDone();
    if (resetsAccounts(dto, form)) return setConfirming(true);
    run();
  };
  const secretHint = form.signInMethod === 'device_code' ? `${t('microsoft365.admin.setup.details.secretHint')} · ${t('microsoft365.admin.setup.details.secretOptional')}` : t('microsoft365.admin.setup.details.secretHint');
  return (
    <form
      className="m365a-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      data-testid="m365-details"
    >
      <p className="m365a-step__intro">{t('microsoft365.admin.setup.details.intro')}</p>
      <Field
        label={t('microsoft365.admin.setup.details.tenantId')}
        hint={t('microsoft365.admin.setup.details.tenantIdHint')}
        error={problem('entraTenantId') ? t('microsoft365.admin.setup.details.guidError') : undefined}
      >
        {({ id, describedBy, invalid }) => (
          <TextInput
            id={id}
            className="m365a-mono"
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            value={form.entraTenantId}
            placeholder={t('microsoft365.admin.setup.details.guidPlaceholder')}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => set({ entraTenantId: e.target.value })}
            data-testid="m365-tenant-id"
          />
        )}
      </Field>
      <Field
        label={t('microsoft365.admin.setup.details.clientId')}
        hint={t('microsoft365.admin.setup.details.clientIdHint')}
        error={problem('clientId') ? t('microsoft365.admin.setup.details.guidError') : undefined}
      >
        {({ id, describedBy, invalid }) => (
          <TextInput
            id={id}
            className="m365a-mono"
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            value={form.clientId}
            placeholder={t('microsoft365.admin.setup.details.guidPlaceholder')}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => set({ clientId: e.target.value })}
            data-testid="m365-client-id"
          />
        )}
      </Field>
      <Field
        label={
          <span className="m365a-label">
            {t('microsoft365.admin.setup.details.secret')}
            {dto.hasSecret ? (
              <span data-testid="m365-secret-saved">
                <Badge tone="success">{t('microsoft365.admin.setup.details.secretSaved')}</Badge>
              </span>
            ) : null}
          </span>
        }
        hint={secretHint}
        error={problem('clientSecret') ? t('microsoft365.admin.setup.details.secretRequired') : undefined}
      >
        {({ id, describedBy, invalid }) => (
          <TextInput
            id={id}
            type="password"
            // never a saved password of the admin's: "new-password" keeps browsers and password managers from filling it
            autoComplete="new-password"
            data-1p-ignore=""
            data-lpignore="true"
            spellCheck={false}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            value={form.clientSecret}
            placeholder={t(dto.hasSecret ? 'microsoft365.admin.setup.details.secretReplace' : 'microsoft365.admin.setup.details.secretPlaceholder')}
            onChange={(e) => set({ clientSecret: e.target.value })}
            data-testid="m365-secret"
          />
        )}
      </Field>
      <Field label={t('microsoft365.admin.setup.details.expires')} hint={t('microsoft365.admin.setup.details.expiresHint')}>
        {({ id, describedBy }) => (
          <TextInput
            id={id}
            type="date"
            className="m365a-date"
            aria-describedby={describedBy}
            value={form.secretExpiresOn}
            onChange={(e) => set({ secretExpiresOn: e.target.value })}
            data-testid="m365-secret-expires"
          />
        )}
      </Field>
      <RedirectBox dto={dto} />
      <details className="m365a-method" open={methodOpen} data-testid="m365-method">
        <summary>{t('microsoft365.admin.setup.details.method')}</summary>
        <p className="m365a-method__hint">{t('microsoft365.admin.setup.details.methodHint')}</p>
        <div className="m365a-method__options" role="radiogroup" aria-label={t('microsoft365.admin.setup.details.methodLabel')}>
          {(['auth_code', 'device_code'] as const).map((method) => (
            <RadioCard
              key={method}
              name="m365-sign-in-method"
              value={method}
              checked={form.signInMethod === method}
              onChange={() => set({ signInMethod: method })}
              title={
                <>
                  {t(`microsoft365.admin.setup.details.methods.${method}.title`)}{' '}
                  {method === 'device_code' && !dto.redirectUriAccepted ? <Badge tone="info">{t('microsoft365.admin.setup.details.recommended')}</Badge> : null}
                </>
              }
              hint={t(`microsoft365.admin.setup.details.methods.${method}.hint`)}
              data-testid={`m365-method-${method}`}
            />
          ))}
        </div>
      </details>
      <ErrorAlert error={confirming ? null : save.error} />
      <footer className="m365a-step__actions">
        <Button variant="secondary" onClick={onBack}>
          {t('microsoft365.admin.setup.back')}
        </Button>
        <Button type="submit" busy={save.isPending} data-testid="m365-save">
          {t('microsoft365.admin.setup.details.save')}
        </Button>
      </footer>
      {confirming ? (
        <ConfirmDialog
          title={t('microsoft365.admin.setup.details.resetTitle')}
          confirmLabel={t('microsoft365.admin.setup.details.resetConfirm')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={save.isPending}
          error={<ErrorAlert error={save.error} />}
          onConfirm={run}
          onClose={() => setConfirming(false)}
          data-testid="m365-reset-confirm"
        >
          {t('microsoft365.admin.setup.details.resetBody', { count: dto.connectedCount })}
        </ConfirmDialog>
      ) : null}
    </form>
  );
}

/** The redirect address exactly as the server computes it (from APP_ORIGIN), never from this page's address. */
function RedirectBox({ dto }: { dto: Microsoft365AdminDto }) {
  const { t } = useTranslation();
  return (
    <div className={`m365a-redirect${dto.redirectUriAccepted ? '' : ' m365a-redirect--http'}`} data-testid="m365-redirect">
      <strong>{t('microsoft365.admin.setup.details.redirectTitle')}</strong>
      <span className="m365a-redirect__path">{t('microsoft365.admin.setup.details.redirectPath')}</span>
      <div className="m365a-redirect__value">
        <code data-testid="m365-redirect-uri">{dto.redirectUri}</code>
        <CopyButton text={dto.redirectUri} testId="m365-redirect-copy" />
      </div>
      {dto.redirectUriAccepted ? null : (
        <p className="m365a-redirect__warning" data-testid="m365-redirect-http">
          <Icons.warning size={16} />
          <span>{t('microsoft365.admin.setup.details.redirectHttp')}</span>
        </p>
      )}
    </div>
  );
}

/**
 * 3 · Admin consent: Microsoft's own page for the read permissions; the browser comes back to the summary
 * (?consent=granted|denied). Without an https address Microsoft cannot come back, so it is given in Entra.
 */
function ConsentStep({ dto, onBack, onNext }: { dto: Microsoft365AdminDto; onBack: () => void; onNext: () => void }) {
  const { t } = useTranslation();
  const consent = useMicrosoft365AdminConsent();
  const open = () =>
    consent.mutate(undefined, {
      onSuccess: ({ url }) => {
        if (/^https?:\/\//i.test(url)) window.location.assign(url);
      },
    });
  return (
    <>
      <p className="m365a-step__intro">{t('microsoft365.admin.setup.consent.intro')}</p>
      <ul className="m365a-perms">
        <li>
          <Icons.user size={16} />
          <span>{t('microsoft365.admin.setup.consent.permissions.user')}</span>
          <code>User.Read</code>
        </li>
        <li>
          <Icons.mail size={16} />
          <span>{t('microsoft365.admin.setup.consent.permissions.mail')}</span>
          <code>Mail.Read</code>
        </li>
        <li>
          <Icons.calendar size={16} />
          <span>{t('microsoft365.admin.setup.consent.permissions.calendar')}</span>
          <code>Calendars.Read</code>
        </li>
      </ul>
      <p className="m365a-step__muted">{t('microsoft365.admin.setup.consent.who')}</p>
      {dto.redirectUriAccepted ? (
        <div className="m365a-step__cta">
          <Button icon={<Icons.external size={16} />} busy={consent.isPending} onClick={open} data-testid="m365-admin-consent">
            {t('microsoft365.admin.setup.consent.button')}
          </Button>
        </div>
      ) : (
        <Alert tone="info" title={t('microsoft365.admin.setup.consent.noRedirectTitle')} data-testid="m365-consent-portal">
          {t('microsoft365.admin.setup.consent.noRedirect')}
        </Alert>
      )}
      <ErrorAlert error={consent.error} />
      <p className="m365a-step__muted">{t('microsoft365.admin.setup.consent.already')}</p>
      <footer className="m365a-step__actions">
        <Button variant="secondary" onClick={onBack}>
          {t('microsoft365.admin.setup.back')}
        </Button>
        <Button variant={dto.redirectUriAccepted ? 'secondary' : 'primary'} onClick={onNext} data-testid="m365-step-next">
          {t('microsoft365.admin.setup.next')}
        </Button>
      </footer>
    </>
  );
}

/** 4 · The test: tenant, app and secret at Microsoft, in plain words; what to do next. */
function TestStep({ dto, onBack, onFix }: { dto: Microsoft365AdminDto; onBack: () => void; onFix: (step: SetupStep) => void }) {
  const { t } = useTranslation();
  const test = useMicrosoft365Test();
  const passed = test.data?.ok === true;
  return (
    <>
      <p className="m365a-step__intro">{t('microsoft365.admin.setup.test.intro')}</p>
      <div className="m365a-step__cta">
        <Button variant={passed ? 'secondary' : 'primary'} busy={test.isPending} onClick={() => test.mutate()} data-testid="m365-run-test">
          {t('microsoft365.admin.setup.test.button')}
        </Button>
      </div>
      {test.data ? <TestOutcome result={test.data} onFix={onFix} /> : null}
      <ErrorAlert error={test.error} />
      {passed ? (
        <Alert tone="success" title={t('microsoft365.admin.setup.test.finishTitle')} data-testid="m365-finished">
          {t(dto.enabled ? 'microsoft365.admin.setup.test.finishOn' : 'microsoft365.admin.setup.test.finishOff')}
        </Alert>
      ) : null}
      <footer className="m365a-step__actions">
        <Button variant="secondary" onClick={onBack}>
          {t('microsoft365.admin.setup.back')}
        </Button>
        <Link className={`ui-button ${passed ? 'ui-button--primary' : 'ui-button--secondary'}`} to={SUMMARY_PATH} data-testid="m365-to-summary">
          {t('microsoft365.admin.setup.test.toSummary')}
        </Link>
      </footer>
    </>
  );
}
